/* Header predictive search panel: open/close, debounce, and per-type Shopify search. */
if (!customElements.get('wlsn-header-search')) {
  /*
   * Both navs are always in the DOM; CSS picks one per width. querySelector would return the
   * new nav every time (first in DOM order), so below 768px the panel would measure a hidden
   * element. getClientRects() is empty when hidden and, unlike offsetParent, survives fixed.
   */
  const resolveVisibleHeader = () => {
    const headers = document.querySelectorAll('.wlsn-header-nav, .wlsn-header');
    return Array.from(headers).find((header) => header.getClientRects().length > 0) || null;
  };

  class WlsnHeaderSearch extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        open: 'is-open',
        searching: 'is-searching',
        loading: 'is-loading',
        trigger: 'js-wlsn-search-trigger'
      };

      this.cache = {};
      this.controller = null;
      this.debounceTimer = null;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onClear = this.onClear.bind(this);
      this.reposition = this.reposition.bind(this);
      this.close = this.close.bind(this);
    }

    connectedCallback() {
      this.panel = this.querySelector('.js-wlsn-search-panel');
      this.overlay = this.querySelector('.js-wlsn-search-overlay');
      this.form = this.querySelector('.js-wlsn-search-form');
      this.input = this.querySelector('.js-wlsn-search-input');
      this.clearBtn = this.querySelector('.js-wlsn-search-clear');
      this.closeBtn = this.querySelector('.js-wlsn-search-close');
      this.results = this.querySelector('.js-wlsn-search-results');
      this.defaultContent = this.querySelector('.js-wlsn-search-default');

      // Header resolved per reposition() call - which one is visible changes on resize.

      this.searchUrl = this.dataset.searchUrl || '/search';
      this.sectionId = this.dataset.sectionId || 'wlsn-predictive-search';
      this.minChars = parseInt(this.dataset.minChars, 10) || 2;
      this.types = ['product', 'page', 'article'];

      // Trigger may live in another section — handled via document click delegation.
      if (this.overlay) this.overlay.addEventListener('click', this.close);
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.close);
      if (this.clearBtn) this.clearBtn.addEventListener('click', this.onClear);
      if (this.input) this.input.addEventListener('input', this.onInput);
      if (this.form) this.form.addEventListener('submit', this.onSubmit);

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('wlsn:nav:open', this.close);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('wlsn:nav:open', this.close);
      window.removeEventListener('resize', this.reposition);
      window.removeEventListener('scroll', this.reposition);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.reposition);
        window.visualViewport.removeEventListener('scroll', this.reposition);
      }
    }

    get isOpen() {
      return this.classList.contains(this.classes.open);
    }

    get triggers() {
      return Array.from(document.querySelectorAll(`.${this.classes.trigger}`));
    }

    open() {
      document.dispatchEvent(new CustomEvent('wlsn:search:open'));
      this.reposition();
      this.classList.add(this.classes.open);
      document.body.style.overflow = 'hidden';
      this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
      window.addEventListener('resize', this.reposition);
      window.addEventListener('scroll', this.reposition, { passive: true });
      // Mobile keyboard resizes visualViewport, not window.
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.reposition);
        window.visualViewport.addEventListener('scroll', this.reposition);
      }
      window.setTimeout(() => {
        if (this.input) this.input.focus();
      }, 60);
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove(this.classes.open);
      document.body.style.overflow = '';
      this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      window.removeEventListener('resize', this.reposition);
      window.removeEventListener('scroll', this.reposition);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.reposition);
        window.visualViewport.removeEventListener('scroll', this.reposition);
      }
    }

    reposition() {
      // Fresh each call: resize is exactly when the visible nav swaps.
      this.header = resolveVisibleHeader();
      if (!this.header) return;

      const anchor = this.header.closest('.shopify-section') || this.header;
      const bottom = Math.max(anchor.getBoundingClientRect().bottom, 0);
      const viewport = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      this.style.setProperty('--wlsn-search-top', `${bottom}px`);
      this.style.setProperty('--wlsn-search-max-height', `${Math.max(viewport - bottom, 0)}px`);
    }

    onDocumentClick(event) {
      const trigger = event.target.closest && event.target.closest(`.${this.classes.trigger}`);
      if (trigger) {
        event.preventDefault();
        if (this.isOpen) {
          this.close();
        } else {
          this.open();
        }
        return;
      }

      if (!this.isOpen) return;
      if (this.panel && this.panel.contains(event.target)) return;
      this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) this.close();
    }

    onSubmit(event) {
      if (!this.input || this.input.value.trim().length === 0) {
        event.preventDefault();
      }
    }

    onInput() {
      const term = this.input.value.trim();

      this.toggleClear(this.input.value.length > 0);

      if (term.length < this.minChars) {
        window.clearTimeout(this.debounceTimer);
        this.showDefault();
        return;
      }

      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = window.setTimeout(() => this.search(term), 250);
    }

    onClear() {
      if (this.controller) this.controller.abort();
      window.clearTimeout(this.debounceTimer);
      if (this.input) {
        this.input.value = '';
        this.input.focus();
      }
      this.toggleClear(false);
      this.showDefault();
    }

    toggleClear(show) {
      if (this.clearBtn) this.clearBtn.hidden = !show;
    }

    showDefault() {
      this.classList.remove(this.classes.searching, this.classes.loading);
      if (this.results) {
        this.results.hidden = true;
        this.results.innerHTML = '';
      }
      if (this.defaultContent) this.defaultContent.hidden = false;
    }

    showResults(html) {
      if (this.results) {
        this.results.innerHTML = html;
        this.results.hidden = false;
      }
      if (this.defaultContent) this.defaultContent.hidden = true;
      this.classList.add(this.classes.searching);
      this.classList.remove(this.classes.loading);
    }

    async search(term) {
      if (this.cache[term] !== undefined) {
        this.showResults(this.cache[term]);
        return;
      }

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.classList.add(this.classes.loading);

      try {
        const columns = await Promise.all(this.types.map((type) => this.fetchColumn(term, type)));
        const html = this.buildResults(term, columns);
        this.cache[term] = html;
        this.showResults(html);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error);
          this.showDefault();
        }
      }
    }

    async fetchColumn(term, type) {
      const params = new URLSearchParams({
        q: this.buildQuery(term),
        type,
        section_id: this.sectionId
      });

      const response = await fetch(`${this.searchUrl}?${params.toString()}`, {
        signal: this.controller.signal
      });
      if (!response.ok) throw new Error(`Search failed: ${response.status}`);

      const text = await response.text();
      const column = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#wlsn-predictive-column');

      if (!column || (parseInt(column.dataset.count, 10) || 0) === 0) return null;
      column.removeAttribute('id');

      const limit = parseInt(column.dataset.displayLimit, 10);
      if (!Number.isNaN(limit) && limit > 0) {
        const selectors = {
          product: '.wlsn-predictive__products-grid > .wlsn-product-card',
          page: '.wlsn-predictive__links > .wlsn-predictive__link-item',
          article: '.wlsn-predictive__links > .wlsn-predictive__link-item'
        };
        Array.from(column.querySelectorAll(selectors[type]))
          .slice(limit)
          .forEach((item) => item.remove());
      }

      return column;
    }

    buildResults(term, columns) {
      const byType = {};
      columns.forEach((column) => {
        if (column) byType[column.dataset.type] = column.outerHTML;
      });

      if (Object.keys(byType).length === 0) {
        return `<div class="wlsn-predictive"><p class="wlsn-predictive__empty wlsn-text-body-regular">No results found for &quot;${this.escapeHtml(term)}&quot;.</p></div>`;
      }

      const left = [byType.page, byType.article].filter(Boolean).join('');
      const right = byType.product || '';

      return [
        '<div class="wlsn-predictive">',
        left ? `<div class="wlsn-predictive__left">${left}</div>` : '',
        right ? `<div class="wlsn-predictive__right">${right}</div>` : '',
        '</div>'
      ].join('');
    }

    buildQuery(term) {
      return term.trim().replace(/\s+/g, ' ');
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = value;
      return div.innerHTML;
    }
  }

  customElements.define('wlsn-header-search', WlsnHeaderSearch);
}
